import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useUXState, UXStateType } from '@/context/UXStateContext';
import {
  FileQuestion,
  ShieldAlert,
  ServerCrash,
  Wrench,
  WifiOff,
  Inbox,
  SearchX,
  Loader2,
  AlertCircle,
  CheckCircle2,
  KeyRound,
  X,
  ArrowRight,
  Mail,
  RefreshCw,
  Home,
} from 'lucide-react';

interface StateConfig {
  icon: any;
  color: string;
  badgeBg: string;
  badgeText: string;
  badgeLabel: string;
}

const STATE_CONFIGS: Record<UXStateType, StateConfig> = {
  '404': {
    icon: FileQuestion,
    color: '#6d28d9',
    badgeBg: 'rgba(109, 40, 217, 0.1)',
    badgeText: '#6d28d9',
    badgeLabel: '404 NOT FOUND',
  },
  '403': {
    icon: ShieldAlert,
    color: '#ea580c',
    badgeBg: 'rgba(234, 88, 12, 0.1)',
    badgeText: '#ea580c',
    badgeLabel: '403 FORBIDDEN',
  },
  '500': {
    icon: ServerCrash,
    color: '#dc2626',
    badgeBg: 'rgba(220, 38, 38, 0.1)',
    badgeText: '#dc2626',
    badgeLabel: '500 SERVER ERROR',
  },
  'maintenance': {
    icon: Wrench,
    color: '#d97706',
    badgeBg: 'rgba(217, 119, 6, 0.1)',
    badgeText: '#d97706',
    badgeLabel: 'MAINTENANCE',
  },
  'offline': {
    icon: WifiOff,
    color: '#475569',
    badgeBg: 'rgba(71, 85, 105, 0.1)',
    badgeText: '#475569',
    badgeLabel: 'OFFLINE',
  },
  'empty': {
    icon: Inbox,
    color: '#64748b',
    badgeBg: 'rgba(100, 116, 139, 0.1)',
    badgeText: '#64748b',
    badgeLabel: 'EMPTY STATE',
  },
  'no_search_results': {
    icon: SearchX,
    color: '#6d28d9',
    badgeBg: 'rgba(109, 40, 217, 0.1)',
    badgeText: '#6d28d9',
    badgeLabel: 'ZERO MATCHES',
  },
  'loading': {
    icon: Loader2,
    color: '#2563eb',
    badgeBg: 'rgba(37, 99, 235, 0.1)',
    badgeText: '#2563eb',
    badgeLabel: 'SCANNING RADAR',
  },
  'error': {
    icon: AlertCircle,
    color: '#dc2626',
    badgeBg: 'rgba(220, 38, 38, 0.1)',
    badgeText: '#dc2626',
    badgeLabel: 'ACTION ERROR',
  },
  'success': {
    icon: CheckCircle2,
    color: '#16a34a',
    badgeBg: 'rgba(22, 163, 74, 0.1)',
    badgeText: '#16a34a',
    badgeLabel: 'SUCCESS CONFIRMED',
  },
  'session_expired': {
    icon: KeyRound,
    color: '#111111',
    badgeBg: 'rgba(17, 17, 17, 0.1)',
    badgeText: '#111111',
    badgeLabel: 'SESSION EXPIRED',
  },
};

export default function UXStateModal() {
  const { activeUX, dismissUX, openAuthModal } = useUXState();
  const navigate = useNavigate();

  if (!activeUX) return null;

  const cfg = STATE_CONFIGS[activeUX.state] || STATE_CONFIGS['error'];
  const IconComponent = cfg.icon;
  const isSpinning = activeUX.state === 'loading';

  const handleAction = () => {
    if (activeUX.onAction) {
      activeUX.onAction();
      dismissUX();
      return;
    }

    switch (activeUX.actionType) {
      case 'RELOGIN':
        openAuthModal();
        break;
      case 'GO_HOME':
        dismissUX();
        navigate('/');
        break;
      case 'CONTACT_SUPPORT':
        dismissUX();
        window.location.href = `mailto:${activeUX.contactEmail || 'ashutosh4tech@gmail.com'}?subject=KhelDrop%20Error%20Report&body=I%20encountered%20a%20server%20error%20on%20KhelDrop.`;
        break;
      case 'CLEAR_FILTERS':
        dismissUX();
        navigate('/');
        break;
      case 'RETRY':
        dismissUX();
        window.location.reload();
        break;
      case 'DISMISS':
      default:
        dismissUX();
        break;
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        background: 'rgba(10, 10, 15, 0.45)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        animation: 'fadeIn 0.2s ease',
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ux-state-title"
    >
      <div
        style={{
          background: 'var(--surface, #ffffff)',
          border: '1px solid var(--border, #e8e8ed)',
          borderRadius: '24px',
          maxWidth: '460px',
          width: '100%',
          padding: '28px',
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.16)',
          position: 'relative',
          animation: 'slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Close Button */}
        <button
          onClick={dismissUX}
          aria-label="Close notification"
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'var(--elevated, #f4f4f6)',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-secondary, #6b6b76)',
            cursor: 'pointer',
            transition: 'background 0.15s',
          }}
        >
          <X size={16} />
        </button>

        {/* State Badge & Icon */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '18px' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '16px',
              background: cfg.badgeBg,
              color: cfg.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <IconComponent size={24} className={isSpinning ? 'animate-spin' : ''} />
          </div>
          <div>
            <div
              style={{
                fontSize: '11px',
                fontWeight: 800,
                letterSpacing: '0.08em',
                color: cfg.badgeText,
                textTransform: 'uppercase',
                marginBottom: '2px',
              }}
            >
              {cfg.badgeLabel}
            </div>
            <h3
              id="ux-state-title"
              style={{
                fontSize: '18px',
                fontWeight: 800,
                color: 'var(--text, #0a0a0b)',
                margin: 0,
                letterSpacing: '-0.01em',
              }}
            >
              {activeUX.title}
            </h3>
          </div>
        </div>

        {/* Message Body */}
        <p
          style={{
            fontSize: '13.5px',
            lineHeight: 1.55,
            color: 'var(--text-secondary, #6b6b76)',
            margin: '0 0 22px',
          }}
        >
          {activeUX.message}
        </p>

        {/* Support Callout if 500 or Error */}
        {(activeUX.state === '500' || activeUX.contactEmail) && (
          <div
            style={{
              background: '#fafafb',
              border: '1px solid var(--border, #e8e8ed)',
              borderRadius: '12px',
              padding: '10px 14px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '12px',
            }}
          >
            <span style={{ color: 'var(--text-secondary, #6b6b76)' }}>Official Support:</span>
            <a
              href="mailto:ashutosh4tech@gmail.com"
              style={{
                color: '#6d28d9',
                fontWeight: 700,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <Mail size={12} /> ashutosh4tech@gmail.com
            </a>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px' }}>
          <button
            onClick={dismissUX}
            style={{
              background: 'transparent',
              border: '1px solid var(--border, #e8e8ed)',
              color: 'var(--text-secondary, #6b6b76)',
              borderRadius: '999px',
              padding: '9px 18px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Dismiss
          </button>

          <button
            onClick={handleAction}
            style={{
              background: cfg.color === '#111111' ? '#111111' : cfg.color,
              color: '#ffffff',
              border: 'none',
              borderRadius: '999px',
              padding: '9px 20px',
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
            }}
          >
            {activeUX.actionText || 'Proceed'}
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
